import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowLeft, DollarSign } from 'lucide-react';
import { PageSpinner } from '../../components/PageSpinner';
import { Pagination } from '../../components/Pagination';
import { formatDateUTC } from '../../lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ITEMS_PER_PAGE = 15;

export default function AdminRenewals() {
  const navigate = useNavigate();
  const [renewals, setRenewals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [showPayModal, setShowPayModal] = useState(false);
  const [selectedMembership, setSelectedMembership] = useState(null);
  const [paymentData, setPaymentData] = useState({
    valor_pagado: 0,
    medio_pago: 'Transferencia',
    fecha_pago: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    fetchRenewals();
  }, []);

  const fetchRenewals = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const response = await axios.get(`${API_URL}/admin/renewals`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRenewals(response.data);
    } catch (error) {
      toast.error('Error al cargar renovaciones');
    } finally {
      setLoading(false);
    }
  };

  const handlePayClick = (membership) => {
    setSelectedMembership(membership);
    setPaymentData({ ...paymentData, valor_pagado: membership.valor });
    setShowPayModal(true);
  };

  const handlePayment = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.post(`${API_URL}/admin/payments`, {
        company_id: selectedMembership.company_id,
        membership_id: selectedMembership.id,
        ...paymentData,
        tipo_membresia: selectedMembership.tipo_membresia
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Pago registrado exitosamente');
      setShowPayModal(false);
      fetchRenewals();
    } catch (error) {
      toast.error('Error al registrar pago');
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-8 py-6 flex items-center gap-4">
          <button onClick={() => navigate('/admin/dashboard')} className="p-2 hover:bg-slate-100 rounded-lg"><ArrowLeft size={24} /></button>
          <div><h1 className="text-2xl font-bold text-slate-900">Renovaciones de Membresías</h1></div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-8 py-8">
        {loading ? (
          <PageSpinner />
        ) : (
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-slate-50 border-b border-slate-200">
                <tr>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Razón Social</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Documento</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Fecha Renovación</th>
                  <th className="px-6 py-4 text-center text-sm font-semibold text-slate-900">Días Vencidos</th>
                  <th className="px-6 py-4 text-left text-sm font-semibold text-slate-900">Membresía</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Valor</th>
                  <th className="px-6 py-4 text-right text-sm font-semibold text-slate-900">Acciones</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {renewals.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((renewal) => (
                  <tr key={renewal.id} className="hover:bg-slate-50">
                    <td className="px-6 py-4 text-sm text-slate-900">{renewal.razon_social}</td>
                    <td className="px-6 py-4 text-sm text-slate-600 font-mono">{renewal.numero_documento}</td>
                    <td className="px-6 py-4 text-sm text-slate-600">{formatDateUTC(renewal.fecha_renovacion)}</td>
                    <td className="px-6 py-4 text-center">
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        renewal.dias_vencidos > 0 ? 'bg-red-50 text-red-700' : 'bg-amber-50 text-amber-700'
                      }`}>
                        {renewal.dias_vencidos > 0 ? `${renewal.dias_vencidos} días` : 'Próximo'}
                      </span>
                    </td>
                    <td className="px-6 py-4"><span className="px-3 py-1 bg-violet-100 text-violet-700 rounded-full text-xs font-semibold">{renewal.tipo_membresia}</span></td>
                    <td className="px-6 py-4 text-right text-sm font-semibold text-slate-900">${renewal.valor?.toLocaleString()}</td>
                    <td className="px-6 py-4 text-right">
                      <button onClick={() => handlePayClick(renewal)} className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-semibold transition-colors">Pagar</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div className="px-6 py-4">
              <Pagination currentPage={currentPage} totalPages={Math.ceil(renewals.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
            </div>
          </div>
        )}

        {showPayModal && selectedMembership && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl max-w-md w-full p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Registrar Pago</h2>
              <p className="text-slate-600 mb-6">Empresa: <strong>{selectedMembership.razon_social}</strong></p>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Valor a Pagar</label>
                  <input type="number" value={paymentData.valor_pagado} onChange={(e) => setPaymentData({ ...paymentData, valor_pagado: parseFloat(e.target.value) })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Medio de Pago</label>
                  <select value={paymentData.medio_pago} onChange={(e) => setPaymentData({ ...paymentData, medio_pago: e.target.value })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500">
                    <option value="Transferencia">Transferencia</option>
                    <option value="Efectivo">Efectivo</option>
                    <option value="Pago Online">Pago Online</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Fecha de Pago</label>
                  <input type="date" value={paymentData.fecha_pago} onChange={(e) => setPaymentData({ ...paymentData, fecha_pago: e.target.value })} className="w-full px-4 py-3 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-violet-500" />
                </div>
              </div>
              <div className="flex gap-4 mt-6">
                <button onClick={() => setShowPayModal(false)} className="flex-1 px-6 py-3 bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold rounded-lg">Cancelar</button>
                <button onClick={handlePayment} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg"><DollarSign size={20} /> Realizar Pago</button>
              </div>
            </div>
          </div>
        )}
      </main>
    </div>
  );
}
